import { motion } from "motion/react";
import { Icon } from "../../components/Icon";
import { CachedMedia, useCachedMedia } from "./CachedMedia";
import { useT } from "../../i18n/strings";
import { heroSpring } from "../../motion";
import type { Attachment } from "../../api/types";
import s from "../artworks/detail.module.css";

interface MediaHeroProps {
  media: Attachment | null;
  layoutId?: string;
  /** Something is wrong with the signature: refetch the record, throttled. */
  onExpired?: () => void;
}

export function MediaHero({ media, layoutId, onExpired }: MediaHeroProps) {
  const t = useT();
  const isVideo = media?.mime_type.startsWith("video/") ?? false;
  const ratio =
    media?.width &&
    media?.height &&
    Number.isFinite(media.width) &&
    Number.isFinite(media.height) &&
    media.width > 0 &&
    media.height > 0
      ? media.width / media.height
      : null;

  // `.heroFrame` centres whatever is in it, so a glyph needs no wrapper of its
  // own — and must not have one, or the plate's aspect box and its scroll
  // animation are applied twice.
  const glyph = (name: "image" | "noImage") => (
    <Icon name={name} size={48} label={t.detail.noMedia} />
  );

  if (!media) {
    return (
      <motion.div className={s.hero} layoutId={layoutId} transition={heroSpring}>
        <div className={s.heroFrame}>{glyph("image")}</div>
      </motion.div>
    );
  }

  return (
    <motion.div className={s.hero} layoutId={layoutId} transition={heroSpring}>
      <div
        className={s.heroFrame}
        // Reserve the real shape so the page does not jump when the asset lands.
        style={ratio ? { aspectRatio: `${media.width} / ${media.height}` } : undefined}
      >
        {/* The thumbnail the list already had, blown up and thrown out of
            focus: the frame is never empty while the full asset decodes, and
            what fills it is this work's own colour rather than a grey box.
            It comes out of the same cache the list filled, so it costs nothing. */}
        {media.thumbnail_url && !isVideo && (
          <CachedMedia
            key={`blur-${media.id}`}
            plain
            media={media}
            prefer="thumbnail"
            className={s.heroBlur}
            fallback={null}
          />
        )}

        {isVideo ? (
          <VideoHero media={media} onExpired={onExpired} />
        ) : (
          <CachedMedia
            key={`hero-${media.id}`}
            eager
            media={media}
            prefer="display"
            className={s.heroMedia}
            alt={media.caption || ""}
            onUnavailable={onExpired}
            fallback={null}
            brokenFallback={glyph("noImage")}
          />
        )}
      </div>
    </motion.div>
  );
}

/**
 * The clip stays on the network on purpose: `<video>` asks for byte ranges as it
 * plays, and a 200 MB file pulled into a blob first would have to finish
 * downloading before the first frame appeared.
 *
 * Its poster is a different matter. That is an ordinary still, made from a frame
 * of this clip at upload time, and the list already downloaded it — so it comes
 * out of the cache rather than off the wire a second time.
 */
function VideoHero({ media, onExpired }: { media: Attachment; onExpired?: () => void }) {
  const poster = useCachedMedia({ media, prefer: "display", onUnavailable: onExpired });
  return (
    <video
      className={s.heroVideo}
      src={media.url}
      poster={poster.src ?? undefined}
      controls
      playsInline
      preload="metadata"
      onError={() => onExpired?.()}
    />
  );
}
